import { PayloadAction } from '@reduxjs/toolkit'
import { fromJS } from 'immutable'
import { omit } from 'lodash-es'
import { createReducer } from 'redux-immutablejs'

import { FavoritedProvidersSaveEventPayload } from '@lyrahealth-inc/shared-app-logic/src/features/favoritedProviders/types'

import {
  CLEAR_FAVORITED_PROVIDERS_FOR_USER,
  FAVORITE_NEW_PROVIDER,
  REMOVE_FAVORITED_PROVIDER,
  REMOVE_MULTIPLE_FAVORITED_PROVIDERS,
  SET_FAVORITED_PROVIDERS_IDS_FOR_USER,
  SET_FAVORITED_PROVIDERS_METADATA_MAP,
} from './favoritedProvidersActions'

/*
 * favoritedProvidersByIds: An array of provider IDs (string) representing all currently favorited providers.
 * This array is modified anytime a user favorites/unfavorites a provider in the session.
 *
 * favoritedProvidersMetadataMap: A map of provider IDs (string) to favorited providers metadata (like the favorited date and search metadata)
 * This map is populated from backend DB upon login when we fetch the user's currently saved providers.
 * When a user favorite/unfavorites a new provider in the current session, we update this map to include the new provider's metadata, most importantly the createDateUnixTimestamp
 * and the treatmentPreference as these are needed to efficiently fetch availability or filter out ineligible providers when programs or high risk indicators are present.
 */
const initialState = fromJS({
  favoritedProvidersByIds: null,
  favoritedProvidersMetadataMap: null,
})

export default createReducer(initialState, {
  [SET_FAVORITED_PROVIDERS_IDS_FOR_USER]: ($$state: Map<string, any>, action: PayloadAction<string[]>) => {
    return $$state.set('favoritedProvidersByIds', action.payload)
  },
  [SET_FAVORITED_PROVIDERS_METADATA_MAP]: ($$state: $TSFixMe, action: $TSFixMe) => {
    return $$state.set('favoritedProvidersMetadataMap', action.payload)
  },
  [FAVORITE_NEW_PROVIDER]: (
    $$state: Map<string, any>,
    action: PayloadAction<{ providerId: string; data: FavoritedProvidersSaveEventPayload }>,
  ) => {
    const { providerId } = action.payload
    // Add new provider ID to the array of favorited provider IDs
    const favoritedProvidersList = $$state.get('favoritedProvidersByIds') || []
    $$state = $$state.set('favoritedProvidersByIds', favoritedProvidersList.concat(providerId))

    // Update the metadata map with the new data
    const metadataMap = $$state.get('favoritedProvidersMetadataMap') || {}
    const { treatmentPreference, searchId, userFavoriteProviderRequestId } = action.payload.data

    $$state = $$state.set('favoritedProvidersMetadataMap', {
      ...metadataMap,
      [providerId]: {
        createDateUnixTimestamp: Date.now(),
        treatmentPreference,
        searchId,
        userFavoriteProviderRequestId,
      },
    })
    return $$state
  },
  [REMOVE_FAVORITED_PROVIDER]: ($$state: Map<string, any>, action: PayloadAction<string>) => {
    const favoritedProvidersList = $$state.get('favoritedProvidersByIds') || []
    $$state = $$state.set(
      'favoritedProvidersByIds',
      favoritedProvidersList.filter((id: string) => id !== action.payload),
    )
    const metadataMap = $$state.get('favoritedProvidersMetadataMap') || {}
    $$state = $$state.set('favoritedProvidersMetadataMap', omit(metadataMap, action.payload))
    return $$state
  },
  [CLEAR_FAVORITED_PROVIDERS_FOR_USER]: ($$state: Map<string, any>) => {
    $$state = $$state.set('favoritedProvidersByIds', [])
    $$state = $$state.set('favoritedProvidersMetadataMap', {})
    return $$state
  },
  [REMOVE_MULTIPLE_FAVORITED_PROVIDERS]: (
    $$state: Map<string, any>,
    action: PayloadAction<{ providerIds: string[] }>,
  ) => {
    const favoritedProvidersList = $$state.get('favoritedProvidersByIds') || []
    $$state = $$state.set(
      'favoritedProvidersByIds',
      favoritedProvidersList.filter((id: string) => !action.payload.providerIds.includes(id)),
    )
    const metadataMap = $$state.get('favoritedProvidersMetadataMap') || {}
    $$state = $$state.set('favoritedProvidersMetadataMap', omit(metadataMap, action.payload.providerIds))
    return $$state
  },
})
